import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import RevealText from '../motion/RevealText';
import Reveal from '../motion/Reveal';

const APP_STORE_URL = 'https://apps.apple.com/us/app/yunlen/id6751148625';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.yunlen.app';
const QR_URL = `https://quickchart.io/qr?text=${encodeURIComponent(
  PLAY_STORE_URL
)}&size=180&margin=1&dark=121212&light=fcfcfc`;

/**
 * Closing call to action, on the light panel of the section rhythm.
 *
 * Uses the same vocabulary as the rest of the site: mono eyebrow, cascading
 * display headline, hairline rules and a plain QR block rather than a card
 * inside a card.
 */
export default function ReadyToStartCTA() {
  return (
    <section
      data-theme="light"
      className="relative aug-theme aug-grain overflow-hidden py-20 sm:py-28 lg:py-32"
      aria-label="Download the app"
    >
      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-[1.35fr_minmax(0,0.65fr)] gap-12 lg:gap-16 items-end">
          <div>
            <Reveal as="p" className="aug-label text-[var(--theme-muted)] mb-6" y={14}>
              Get started
            </Reveal>

            <RevealText as="h2" className="aug-h1 aug-cascade aug-cascade-tight">
              <span>Ready to start</span>
              <span>your journey?</span>
            </RevealText>

            <Reveal as="p" className="aug-body-lg mt-8 max-w-md text-[var(--theme-muted)]" y={20}>
              Join 50K+ users moving money faster and cheaper. Open an account in minutes.
            </Reveal>

            <Reveal className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4" y={20} delay={0.08}>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center transition-transform duration-300 hover:-translate-y-0.5"
              >
                <img src="/storeImages/Playstore.webp" alt="Get it on Google Play" className="h-12 sm:h-14 w-auto" />
              </a>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center transition-transform duration-300 hover:-translate-y-0.5"
              >
                <img src="/storeImages/AppStore.webp" alt="Download on the App Store" className="h-12 sm:h-14 w-auto" />
              </a>
            </Reveal>
          </div>

          {/* Scan block — hairline framed, no nested cards */}
          <Reveal className="hidden md:block" y={24} delay={0.12}>
            <div className="border-t border-[var(--theme-line)] pt-8">
              <p className="aug-label text-[var(--theme-muted)] mb-6">Scan to download</p>
              <div className="block w-fit p-3 border border-[var(--theme-line)] rounded-2xl bg-[var(--color-white)]">
                <img
                  src={QR_URL}
                  alt="QR code linking to the Yunlen app download"
                  className="w-[148px] h-[148px] block rounded-lg"
                />
              </div>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="aug-link aug-label mt-6"
              >
                Or open the store
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
